import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AppShell } from '../components/AppShell';
import { QuickAddFAB } from '../components/QuickAddFAB';
import { SecurityFormSheet } from '../components/securite/SecurityFormSheet';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus, ShieldCheck, Warning, CheckCircle, Clock,
  MagnifyingGlass, PencilSimple, Trash, ArrowLeft
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function SecuritePage() {
  const navigate = useNavigate();
  const [observations, setObservations] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingObs, setEditingObs] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    chantier_id: '', title: '', description: '', severity: 'medium', type: 'security'
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [obsRes, chRes] = await Promise.all([
        axios.get(`${API}/observations`, { withCredentials: true }),
        axios.get(`${API}/chantiers`, { withCredentials: true })
      ]);
      setObservations(obsRes.data || []);
      setChantiers((chRes.data || []).filter(c => c && c.id && c.name));
    } catch (error) {
      setObservations([]);
      setChantiers([]);
    } finally { setLoading(false); }
  }, [setObservations, setChantiers, setLoading]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateSheet = () => {
    setEditingObs(null);
    setFormData({ chantier_id: '', title: '', description: '', severity: 'medium', type: 'security' });
    setIsSheetOpen(true);
  };

  const openEditSheet = (obs) => {
    setEditingObs(obs);
    setFormData({
      chantier_id: obs.chantier_id || '', title: obs.title || '',
      description: obs.description || '', severity: obs.severity || 'medium', type: 'security'
    });
    setIsSheetOpen(true);
  };

  const handleSubmit = async (e, selectedPhotos = []) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingObs) {
        await axios.put(`${API}/observations/${editingObs.id}`, formData, { withCredentials: true });
      } else {
        const response = await axios.post(`${API}/observations`, formData, { withCredentials: true });
        if (selectedPhotos.length > 0) {
          for (const photo of selectedPhotos) {
            const fd = new FormData();
            fd.append('file', photo);
            await axios.post(
              `${API}/upload?observation_id=${response.data.id}&chantier_id=${formData.chantier_id}`,
              fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
            );
          }
        }
      }
      setIsSheetOpen(false);
      setEditingObs(null);
      fetchData();
    } catch (_err) { toast.error('Erreur sauvegarde observation'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (obsId) => {
    try {
      await axios.delete(`${API}/observations/${obsId}`, { withCredentials: true });
      setDeleteConfirm(null);
      fetchData();
    } catch (_err) { toast.error('Erreur suppression observation'); }
  };

  const getSeverityIcon = (severity) => {
    const map = {
      high: <Warning size={24} className="text-red-500" weight="fill" />,
      medium: <Clock size={24} className="text-amber-500" weight="fill" />,
      low: <CheckCircle size={24} className="text-emerald-500" weight="fill" />,
    };
    return map[severity] || map.medium;
  };

  const getSeverityBadge = (severity) => {
    const map = {
      high: <span className="chantix-badge chantix-badge-error">Critique</span>,
      medium: <span className="chantix-badge chantix-badge-warning">Moderee</span>,
      low: <span className="chantix-badge chantix-badge-success">Faible</span>,
    };
    return map[severity] || map.medium;
  };

  const getChantierName = (chantierId) =>
    (chantiers || []).find(c => c && c.id === chantierId)?.name || 'N/A';

  const filteredObservations = (observations || []).filter(obs => {
    if (!obs) return false;
    const s = searchTerm.toLowerCase();
    return ((obs.title || '').toLowerCase().includes(s) || (obs.description || '').toLowerCase().includes(s))
      && (filterSeverity === 'all' || obs.severity === filterSeverity);
  });

  if (loading) {
    return <AppShell><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-10 h-10 animate-spin text-[#FF6B00]" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="sécurité-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden"
              onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft size={22} />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#111827]">Sécurité</h1>
              <p className="text-[#4B5563] mt-1">{observations.length} observation(s)</p>
            </div>
          </div>
          <Button onClick={openCreateSheet} className="bg-[#FF6B00] hover:bg-orange-600" data-testid="new-obs-btn">
            <Plus size={20} className="mr-2" /> Nouvelle observation
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="chantix-card bg-red-50 border-red-200">
            <CardContent className="p-4 text-center">
              <Warning size={28} className="text-red-500 mx-auto mb-2" weight="fill" />
              <p className="text-2xl font-bold text-red-700">{(observations || []).filter(o => o && o.severity === 'high').length}</p>
              <p className="text-sm text-red-600">Critiques</p>
            </CardContent>
          </Card>
          <Card className="chantix-card bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-center">
              <Clock size={28} className="text-amber-500 mx-auto mb-2" weight="fill" />
              <p className="text-2xl font-bold text-amber-700">{(observations || []).filter(o => o && o.severity === 'medium').length}</p>
              <p className="text-sm text-amber-600">Moderees</p>
            </CardContent>
          </Card>
          <Card className="chantix-card bg-emerald-50 border-emerald-200">
            <CardContent className="p-4 text-center">
              <CheckCircle size={28} className="text-emerald-500 mx-auto mb-2" weight="fill" />
              <p className="text-2xl font-bold text-emerald-700">{(observations || []).filter(o => o && o.severity === 'low').length}</p>
              <p className="text-sm text-emerald-600">Faibles</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
            <Input placeholder="Rechercher..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-14" data-testid="obs-search-input" />
          </div>
          <Select value={filterSeverity} onValueChange={setFilterSeverity}>
            <SelectTrigger className="w-full md:w-40 h-14" data-testid="filter-severity-select">
              <SelectValue placeholder="Gravite" />
            </SelectTrigger>
            <SelectContent position="popper" className="z-[100]">
              <SelectItem value="all" className="py-3">Toutes</SelectItem>
              <SelectItem value="high" className="py-3">Critiques</SelectItem>
              <SelectItem value="medium" className="py-3">Moderees</SelectItem>
              <SelectItem value="low" className="py-3">Faibles</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="grid gap-4">
          {filteredObservations.length > 0 ? filteredObservations.map((obs) => (
            <Card key={obs.id}
              className={`chantix-card ${obs.severity === 'high' ? 'border-l-4 border-l-red-500' : obs.severity === 'medium' ? 'border-l-4 border-l-amber-500' : 'border-l-4 border-l-emerald-500'}`}
              data-testid={`obs-card-${obs.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 rounded-lg bg-gray-100 flex-shrink-0">{getSeverityIcon(obs.severity)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-bold text-[#111827]">{obs.title || 'Sans titre'}</h3>
                      {getSeverityBadge(obs.severity)}
                    </div>
                    <p className="text-sm text-[#4B5563] mb-2">{obs.description || ''}</p>
                    <div className="flex flex-wrap gap-3 text-sm text-[#4B5563]">
                      <span className="text-[#0055FF] font-medium">{getChantierName(obs.chantier_id)}</span>
                      <span>{obs.created_at ? new Date(obs.created_at).toLocaleDateString('fr-FR') : ''}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="icon" onClick={() => openEditSheet(obs)}
                      className="h-12 w-12" data-testid={`edit-obs-${obs.id}`}><PencilSimple size={20} /></Button>
                    <Button variant="outline" size="icon" onClick={() => setDeleteConfirm(obs)}
                      className="h-12 w-12 text-red-600 hover:bg-red-50" data-testid={`delete-obs-${obs.id}`}><Trash size={20} /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card className="chantix-card">
              <CardContent className="py-12 text-center">
                <ShieldCheck size={48} className="mx-auto mb-4 text-[#4B5563] opacity-50" />
                <h3 className="font-semibold text-[#111827] mb-2">Aucune observation</h3>
                <p className="text-[#4B5563] mb-4">Commencez par signaler une observation sécurité</p>
                <Button className="bg-[#FF6B00] hover:bg-orange-600" onClick={openCreateSheet}>
                  <Plus size={20} className="mr-2" /> Nouvelle observation
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <SecurityFormSheet open={isSheetOpen} onOpenChange={setIsSheetOpen}
        editingObs={editingObs} formData={formData} setFormData={setFormData}
        chantiers={chantiers} submitting={submitting} onSubmit={handleSubmit} />

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimér l'observation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimér l'observation "{deleteConfirm?.title}" ? Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm?.id)} className="h-12 bg-red-600 hover:bg-red-700">Supprimér</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QuickAddFAB chantiers={chantiers} />
    </AppShell>
  );
}
